import React from "react";

interface CriScoreDisplayProps {
  scores: Record<string, any>;
  interpretations: Record<string, any>;
}

const CriScoreDisplay: React.FC<CriScoreDisplayProps> = ({ 
  scores, 
  interpretations 
}) => {
  // Define categories for CRI scores
  const categories = [
    {
      title: "Estrategias de Aproximación Cognitivas",
      description: "Esfuerzos cognitivos para resolver o manejar el problema",
      scores: ["analisis_logico", "reevaluacion_positiva"]
    },
    {
      title: "Estrategias de Aproximación Conductuales",
      description: "Esfuerzos conductuales activos para resolver el problema",
      scores: ["busqueda_orientacion", "resolucion_problemas"]
    },
    {
      title: "Estrategias de Evitación Cognitivas",
      description: "Esfuerzos cognitivos para evitar pensar en el problema",
      scores: ["evitacion_cognitiva", "aceptacion_resignacion"]
    },
    {
      title: "Estrategias de Evitación Conductuales",
      description: "Esfuerzos conductuales para evitar enfrentar el problema",
      scores: ["busqueda_recompensas", "descarga_emocional"]
    }
  ];

  // Helper function to get color based on T-score
  const getScoreColor = (tScore: number): string => {
    if (tScore < 40) return "text-blue-500"; // Por debajo del promedio
    if (tScore < 60) return "text-green-500"; // Promedio
    return "text-red-500"; // Por encima del promedio
  };

  // Helper function to get background color based on T-score
  const getBarColor = (tScore: number): string => {
    if (tScore < 40) return "bg-blue-500"; // Por debajo del promedio
    if (tScore < 60) return "bg-green-500"; // Promedio
    return "bg-red-500"; // Por encima del promedio
  };

  // Helper function to get interpretation text
  const getInterpretationText = (key: string, tScore: number): string => {
    // If we have an interpretation from the API, use that
    if (interpretations && interpretations[key]) {
      return interpretations[key];
    }
    
    // Otherwise generate a default interpretation based on T-score
    if (tScore < 40) {
      return "Uso por debajo del promedio";
    } else if (tScore < 60) {
      return "Uso promedio";
    } else {
      return "Uso por encima del promedio";
    }
  };
  
  // Debug log
  console.log("CRI scores:", scores);
  console.log("CRI scores type:", typeof scores);
  console.log("CRI scores keys:", Object.keys(scores));
  console.log("CRI interpretations:", interpretations);

  // If scores is empty or not an object, display a message
  if (!scores || typeof scores !== 'object' || Object.keys(scores).length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-gray-600 dark:text-gray-400">
          No hay puntuaciones disponibles para el Inventario de Respuestas de Afrontamiento
        </p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
          Datos recibidos: {JSON.stringify(scores)}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Summary section */}
      <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md mb-6">
        <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
          Inventario de Respuestas de Afrontamiento (CRI)
        </h5>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Evalúa diferentes estrategias que las personas utilizan para afrontar situaciones de estrés
        </p>
      </div>

      {/* Categories */}
      {categories.map((category, categoryIndex) => (
        <div key={categoryIndex} className="mb-6">
          <h5 className="text-lg font-bold text-navy-700 dark:text-white mb-2">
            {category.title}
          </h5>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {category.description}
          </p>

          <div className="space-y-4">
            {category.scores.map((key) => {
              const score = scores[key];
              if (!score) return null;
              
              const tScore = score.t_score || 50; // Default to average if missing
              const rawScore = score.raw_score || 0;
              const interpretationText = getInterpretationText(key, tScore);
              const textColor = getScoreColor(tScore);
              const barColor = getBarColor(tScore);
              
              return (
                <div key={key} className="bg-white dark:bg-navy-800 rounded-lg p-4 shadow-sm">
                  <div className="flex flex-wrap justify-between items-center mb-1">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                      {score.name}
                    </span>
                    <div className="flex items-center">
                      <span className="text-xs text-gray-500 dark:text-gray-400 mr-2">
                        T: {tScore.toFixed(1)}
                      </span>
                      <span className={`text-sm font-medium ${textColor}`}>
                        {interpretationText}
                      </span>
                    </div>
                  </div>
                  
                  <div className="w-full bg-gray-200 dark:bg-navy-700 rounded-full h-2.5 mb-2">
                    <div 
                      className={`h-2.5 rounded-full ${barColor}`} 
                      style={{ width: `${Math.max(5, Math.min(100, (tScore / 100) * 100))}%` }}
                    ></div>
                  </div>
                  
                  <div className="flex justify-between text-xs text-gray-500 px-1 mb-2">
                    <span>20</span>
                    <span>40</span>
                    <span>60</span>
                    <span>80</span>
                  </div>
                  
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    {score.description}
                  </p>
                  
                  <div className="flex items-start mt-3">
                    <div className="text-xs bg-gray-100 dark:bg-navy-900 px-2 py-1 rounded">
                      Puntuación directa: {rawScore}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Legend */}
      <div className="bg-white dark:bg-navy-800 rounded-xl p-5 shadow-md">
        <h5 className="text-md font-bold text-navy-700 dark:text-white mb-3">
          Interpretación de Puntuaciones T
        </h5>
        <div className="space-y-3">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">T &lt; 40:</span> Uso por debajo del promedio de la estrategia de afrontamiento
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-green-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">T 40-60:</span> Uso promedio de la estrategia de afrontamiento
            </span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-red-500 mr-2"></div>
            <span className="text-sm">
              <span className="font-medium">T &gt; 60:</span> Uso por encima del promedio de la estrategia de afrontamiento
            </span>
          </div>
        </div>
        
        <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <h6 className="text-sm font-semibold mb-2">Acerca de las puntuaciones T</h6>
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Las puntuaciones T son valores estandarizados con una media de 50 y una desviación típica de 10.
            Permiten comparar directamente el nivel de uso de las diferentes estrategias de afrontamiento
            respecto a la población de referencia.
          </p>
        </div>
      </div>
    </div>
  );
};

export default CriScoreDisplay; 